import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Flex, HStack, Icon, Skeleton, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { RiCheckLine, RiArrowRightLine, RiArrowLeftLine } from 'react-icons/ri';
import { useGetCampaignQuery, useUpdateCampaignMutation } from 'api/metaWhatsappApi';
import useAppToast from 'components/toast';
import { Banner, CampaignCard, CampaignPage, ErrorState } from '../_ui/components';
import { useCampaignUI, primaryButton } from '../_ui/ui';
import { campaignFromResponse } from '../_lib/campaignState';
import useDraftAutosave from '../hooks/useDraftAutosave';
import campaignError from '../campaignErrors';
import StepAudience from './StepAudience';
import StepMessage from './StepMessage';
import StepSchedule from './StepSchedule';
import StepReview from './StepReview';
const STEPS = [
 { label: 'Audience', hint: 'Choose which leads this campaign should reach.' },
 { label: 'Message', hint: 'Choose an approved message and fill its variables.' },
 { label: 'Delivery', hint: 'Choose the sending numbers and recipient-local delivery times.' },
 { label: 'Review', hint: 'Check the audience, message and delivery plan before submitting.' },
];
export default function CampaignWizard() { const { id } = useParams(); return <CampaignWizardScreen key={id} id={id} />; }
function CampaignWizardScreen({ id }) {
 const ui = useCampaignUI(); const navigate = useNavigate(); const notify = useAppToast();
 const query = useGetCampaignQuery(id, { refetchOnMountOrArgChange: true }); const campaign = campaignFromResponse(query.currentData ?? query.data, id);
 const [update] = useUpdateCampaignMutation(); const editor = useDraftAutosave({ id, campaign, update });
 const [step, setStep] = useState(0); const [maxStep, setMaxStep] = useState(0); const [moving, setMoving] = useState(false);
 const [stepBusy, setStepBusy] = useState(false);
 const goTo = async (index) => {
  if (moving || stepBusy || index < 0 || index >= STEPS.length) return;
  setMoving(true);
  try { await editor.flush(); setStep(index); setMaxStep((current) => Math.max(current, index)); }
  catch (error) { notify.error('Save the draft before continuing', campaignError(error)); }
  finally { setMoving(false); }
 };
 const close = async () => {
  if (moving || stepBusy) return;
  setMoving(true);
  try { await editor.flush(); navigate('/meta-campaigns'); }
  catch (error) { notify.error('Your latest changes have not been saved', campaignError(error)); }
  finally { setMoving(false); }
 };
 // Error before draft guard: a failed GET must not render an endless skeleton.
 if (query.isError && !campaign) return <CampaignPage title='Campaign editor'><ErrorState title='We could not load the draft' onRetry={query.refetch} /></CampaignPage>;
 if (!query.isLoading && !campaign) return <CampaignPage title='Campaign editor'><ErrorState title='No campaign was returned' onRetry={query.refetch} /></CampaignPage>;
 if (!campaign || !editor.draft) return <CampaignPage title='Loading draft'><Skeleton h='400px' borderRadius='2xl' /></CampaignPage>;
 if (campaign.state !== 'DRAFT') return <CampaignPage title={campaign.name}><CampaignCard>
  <ErrorState title='This campaign is no longer an editable draft' description='Open the campaign to view its current status. Editing never bypasses approval or launch.' />
  <Flex justify='center'><Button size='sm' onClick={() => navigate(`/meta-campaigns/${id}`)} {...primaryButton(ui)}>Open campaign</Button></Flex>
 </CampaignCard></CampaignPage>;
 const shared = { campaign, draft: editor.draft, patch: editor.patch, flush: editor.flush, goTo, setStepBusy,
  reloadCampaign: async () => campaignFromResponse(await query.refetch().unwrap(), id) };
 return <CampaignPage title={editor.draft.name || 'Untitled campaign'} subtitle={STEPS[step].hint}
  actions={<HStack spacing={3}>
   <Text fontSize='xs' role='status' color={editor.error ? ui.danger : editor.dirty ? ui.warning : ui.success}>
    {editor.error ? 'Not saved' : editor.saving ? 'Saving...' : editor.dirty ? 'Unsaved changes' : 'All changes saved'}
   </Text><Button variant='neutralOutline' size='sm' onClick={close} isDisabled={stepBusy} isLoading={moving} borderColor={ui.border} color={ui.textPrimary}>Save & close</Button>
  </HStack>}>
  {editor.error && <Box mb={4}><Banner status='error' title='The latest changes were not saved' description={campaignError(editor.error)}>
   <Button size='xs' mt={2} onClick={() => editor.flush().catch((error) => notify.error('Save failed', campaignError(error)))} isLoading={editor.saving}>Retry save</Button>
  </Banner></Box>}
  <CampaignCard p={0} overflow='hidden'>
   <Tabs index={step} onChange={goTo} isLazy lazyBehavior='unmount'>
    <TabList px={3} pt={3} gap={1} borderColor={ui.borderSubtle} overflowX='auto'>
     {STEPS.map((item, index) => <Tab key={item.label} isDisabled={index > maxStep || moving || stepBusy} whiteSpace='nowrap' px={{ base: 3, md: 5 }} py={3}
      color={ui.textSecondary} borderRadius='xl' mb={2} _selected={{ bg: ui.accentSubtle, color: ui.textPrimary, borderColor: 'transparent' }}>
      <HStack spacing={2}><Flex boxSize='24px' borderRadius='full' bg={index === step ? ui.accent : ui.surfaceSubtle} color={index === step ? ui.textOnAccent : ui.textSecondary} align='center' justify='center' fontSize='xs' fontWeight='bold'>
       {index < maxStep ? <Icon as={RiCheckLine} /> : index + 1}
      </Flex><Text fontSize='sm' fontWeight={index === step ? 'semibold' : 'normal'}>{item.label}</Text></HStack>
     </Tab>)}
    </TabList>
    <TabPanels>
     <TabPanel px={{ base: 3, md: 5 }}><StepAudience {...shared} /></TabPanel>
     <TabPanel px={{ base: 3, md: 5 }}><StepMessage {...shared} /></TabPanel>
     <TabPanel px={{ base: 3, md: 5 }}><StepSchedule {...shared} /></TabPanel>
     <TabPanel px={{ base: 3, md: 5 }}><StepReview {...shared} /></TabPanel>
    </TabPanels>
   </Tabs>
   <Flex justify='space-between' px={{ base: 3, md: 5 }} py={4} borderTop='1px solid' borderColor={ui.borderSubtle} bg={ui.surface} gap={3}>
    <Button size='sm' variant='neutralOutline' leftIcon={<RiArrowLeftLine />} isDisabled={step === 0 || moving || stepBusy} onClick={() => goTo(step - 1)} borderColor={ui.border} color={ui.textPrimary}>Back</Button>
    <Text display={{ base: 'none', md: 'block' }} fontSize='xs' color={ui.textMuted} alignSelf='center'>Step {step + 1} of 4 / Changes are saved before continuing</Text>
    {step < 3 && <Button size='sm' rightIcon={<RiArrowRightLine />} onClick={() => goTo(step + 1)} isLoading={moving} loadingText='Saving...' isDisabled={stepBusy} {...primaryButton(ui)}>Continue</Button>}
   </Flex>
  </CampaignCard>
 </CampaignPage>;
}
